import { useState, useEffect, useCallback } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Bot, LogIn, VenetianMask, CheckCircle2, XCircle, Copy } from "lucide-react";

export function CompanionAccountsAdmin({ password }) {
  const [companions, setCompanions] = useState([]);

  useEffect(() => {
    api.get(`/admin/companions?admin_password=${password}`)
      .then((r) => setCompanions(r.data.companions))
      .catch(() => {});
  }, [password]);

  const loginAs = async (c) => {
    try {
      const r = await api.post(`/admin/companions/impersonate?admin_password=${password}`, { user_id: c.user_id });
      localStorage.setItem("token", r.data.access_token);
      toast.success(`Logged in as ${r.data.name} — redirecting...`);
      setTimeout(() => { window.location.href = "/discover"; }, 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impersonation failed");
    }
  };

  return (
    <div className="glass-card p-6 mb-6" data-testid="companion-accounts-section">
      <div className="flex items-center gap-3 mb-4">
        <Bot className="w-6 h-6 text-violet-400" />
        <h2 className="text-lg font-semibold">AI Companion Accounts</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Log in as any AI companion to chat and act as them. You'll be redirected — log out from Settings to return to your own account.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {companions.map((c) => (
          <div key={c.user_id} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white/5 border border-white/10" data-testid={`companion-account-${c.user_id}`}>
            <div className="flex items-center gap-3 min-w-0">
              {(c.picture || c.photos?.[0]) && (
                <img src={c.picture || c.photos[0]} alt={c.name} className="w-10 h-10 rounded-full object-cover" />
              )}
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{c.name}</p>
                <p className="text-xs text-muted-foreground truncate">{c.email}</p>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => loginAs(c)} data-testid={`impersonate-btn-${c.user_id}`}>
              <LogIn className="w-3.5 h-3.5 mr-1.5" /> Login as
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SocialInvitesAdmin({ password }) {
  const [data, setData] = useState({ invites: [], pending: 0 });
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/social-invites?admin_password=${password}`)
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [password]);

  useEffect(() => { load(); }, [load]);

  const update = async (id, status) => {
    setBusy(id);
    try {
      await api.post(`/admin/social-invites/${id}/status?admin_password=${password}`, { status });
      toast.success(status === "sent" ? "Marked as sent — user notified" : "Declined — 50 credits refunded");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setBusy(null);
    }
  };

  const admirerLink = (inv) => `${window.location.origin}/api/admirer/${inv.signup_code}`;

  const admirerMessage = (inv) => {
    const base = inv.invite_type === "date_request"
      ? `💘 Hi! Someone has a big crush on you and asked me to invite you on a date on their behalf. This link is from your secret admirer — sign up to find out who it is: ${admirerLink(inv)}`
      : `💌 Hi! I'm reaching out because someone is secretly admiring you. This link is from your secret admirer — sign up to find out who it is: ${admirerLink(inv)}`;
    return inv.note ? `${base}\n\nThey asked me to mention: "${inv.note}"` : base;
  };

  const badge = (status) => {
    const map = {
      pending: "bg-amber-500/15 text-amber-400 border-amber-500/40",
      sent: "bg-green-500/15 text-green-400 border-green-500/40",
      declined: "bg-red-500/15 text-red-400 border-red-500/40",
    };
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${map[status]}`}>{status.toUpperCase()}</span>;
  };

  return (
    <div className="glass-card p-6 mb-6" data-testid="social-invites-section">
      <div className="flex items-center gap-3 mb-4">
        <VenetianMask className="w-6 h-6 text-pink-400" />
        <h2 className="text-lg font-semibold">Social Secret Admirer Queue {data.pending > 0 && <span className="text-amber-400">({data.pending} pending)</span>}</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Members paid 50 credits for a companion to reach out on social media. Use "Copy message" for a ready-to-send text — its link shows a secret-admirer preview card (companion photo matches the requester's gender) and notifies the requester when the person joins. Then mark it sent. Declining refunds the credits.
      </p>
      {data.invites.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="social-invites-empty">No requests yet.</p>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {data.invites.map((inv) => (
            <div key={inv.request_id} className="p-3 rounded-lg bg-white/5 border border-white/10" data-testid={`social-invite-${inv.request_id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-sm">
                  <span className="font-medium">{inv.companion_name}</span>
                  <span className="text-muted-foreground"> → </span>
                  <span className="font-medium text-primary">{inv.handle}</span>
                  <span className="text-muted-foreground"> on {inv.platform}</span>
                </div>
                {badge(inv.status)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {inv.invite_type === "secret_admirer" ? "💌 Secret admirer" : "💘 Date request"} · requested by {inv.user_name} ({inv.user_email}) · {new Date(inv.created_at).toLocaleString()}
              </p>
              {inv.note && <p className="text-xs italic text-white/70 mt-1">"{inv.note}"</p>}
              {inv.signup_code && (
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <code className="text-[11px] px-2 py-0.5 rounded bg-black/30 border border-white/10 text-white/80 truncate max-w-[280px]">{admirerLink(inv)}</code>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-xs"
                    onClick={() => {
                      navigator.clipboard.writeText(admirerLink(inv));
                      toast.success("Admirer link copied");
                    }}
                    data-testid={`si-copy-link-${inv.request_id}`}
                  >
                    <Copy className="w-3 h-3 mr-1" /> Copy link
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-xs text-pink-300"
                    onClick={() => {
                      navigator.clipboard.writeText(admirerMessage(inv));
                      toast.success("Ready-to-send message copied");
                    }}
                    data-testid={`si-copy-msg-${inv.request_id}`}
                  >
                    <Copy className="w-3 h-3 mr-1" /> Copy message
                  </Button>
                  {inv.invitee_name && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-emerald-500/15 text-emerald-400 border-emerald-500/40" data-testid={`si-admin-joined-${inv.request_id}`}>
                      🎉 Joined: {inv.invitee_name}
                    </span>
                  )}
                </div>
              )}
              {inv.status === "pending" && (
                <div className="flex gap-2 mt-2">
                  <Button size="sm" className="h-7 text-xs" disabled={busy === inv.request_id} onClick={() => update(inv.request_id, "sent")} data-testid={`si-mark-sent-${inv.request_id}`}>
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Mark sent
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs border-red-500/40 text-red-400" disabled={busy === inv.request_id} onClick={() => update(inv.request_id, "declined")} data-testid={`si-decline-${inv.request_id}`}>
                    <XCircle className="w-3 h-3 mr-1" /> Decline & refund
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
